import {useContext, useRef} from 'react';
import {
  TwilioClientProvider as ConversationsTwilioClientProvider,
  TwilioClientProviderV2 as ConversationsTwilioClientProviderV2,
  TwilioClient,
  TwilioClientV2,
} from '@stripe-internal/twilio-conversations-client';
import {
  ClientUrls,
  ObservabilityConfig,
} from '@stripe-internal/conversations-client';
import {ConfigContext} from '../ConfigProvider';
import {useObservability} from '../ObservabilityProvider';
import {useProfile} from '../ProfileProvider';
import {useApi} from '../ApiProvider/ApiProvider';

type Props = {
  children: JSX.Element;
};

export const TwilioClientProvider = ({children}: Props) => {
  const observability = useObservability();
  const {
    audience,
    entrypoint: entryPoint,
    stripeThirdPartyBaseUrl,
  } = useContext(ConfigContext);
  const {profile} = useProfile();
  const {featureFlags} = profile;
  const api = useApi();

  const urls: Partial<ClientUrls> = {
    tokenUrl: `/api/token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/session?audience=${audience}&entrypoint=${entryPoint}`,
  };

  const clientOpts: {
    clientFetch?: typeof fetch;
    urls?: Partial<ClientUrls>;
    observability?: ObservabilityConfig;
  } = {urls, observability};

  if (featureFlags.enablePassFetchToConversationsClient) {
    clientOpts.clientFetch = api.fetch;
  }

  const ref = useRef<TwilioClient | TwilioClientV2>(
    featureFlags.useNewTwilioConversationsClient
      ? new TwilioClientV2(clientOpts)
      : new TwilioClient(clientOpts),
  );
  const client = ref.current;

  return client instanceof TwilioClientV2 ? (
    <ConversationsTwilioClientProviderV2
      value={client}
      observability={observability}
      urls={urls}
      baseURL={stripeThirdPartyBaseUrl}
    >
      {children}
    </ConversationsTwilioClientProviderV2>
  ) : (
    <ConversationsTwilioClientProvider
      value={client}
      observability={observability}
      urls={urls}
      baseURL={stripeThirdPartyBaseUrl}
    >
      {children}
    </ConversationsTwilioClientProvider>
  );
};
